import {formatNumber} from "../../../components/FormatNumber";
import {copyCustomFields} from "./helper/copyCustomFields";
import {deleteAllIds} from "./helper/deleteAllIds";
import {getInitData} from "./helper/getInitData";
import {copyLinkItems} from "./helper/copyLinkItems";
import {PageType} from "../../../enums/PageType";
import {getParentUrl} from "../helpers/getParentUrl";
import { select } from "redux-saga/effects";
import { showConfirmationModalPromise } from "./helper/Modal/showConfirmationModalPromise";
import { processFormItems } from "./helper/processFormItems";
import { SubscriptionCheck } from "../../../components/Access/helper/subscription/SubscriptionCheck";
import { SubscriptionScopeKey } from "../../../components/Access/helper/subscription/enum/SubscriptionScopeKey";
import _ from "lodash";
import { readAutosaveStorage } from "../../LocalStorage/helpers/AutosaveTransactions/readStorage";
import { showWillOverrideAutosaveDataPromise } from "./helper/Modal/showWillOverrideAutosaveDataPromise";

// Case 5: Copy Transaction
// - Different category
// - Supported:
//      - Invoice => Credit Notes
//      - Bill => Bill Supplier Credit
export function* copy(transactionType, originType, originTransaction = {}) {
    // Check if there's any autosaved data for the transaction type, and prompt modal if there is
    const { data, last_saved } = yield readAutosaveStorage(transactionType) || {};

    if (data) {
        const autosaveModalRef = yield select(state => state.Transaction.autosaveModalRef);
        const isProceed = yield showWillOverrideAutosaveDataPromise(transactionType, autosaveModalRef, last_saved, 'copy');
        if (!isProceed)
            return {isProceed: false, transaction: null};
    }

    // Init Data
    const initData = yield getInitData(transactionType);
    const productList = yield select(state => state.List.productList);
    const settings = yield select(state => state.List.settings);
    const companyCurrencyCode = yield select(state => state.Auth.companyCurrencyCode);

    // Link Items
    const {
        linkItems,
        salesLinkItems,
        purchaseLinkItems,
    } = yield copyLinkItems(PageType.copy, transactionType, originTransaction);

    // Custom Fields
    const fields = yield copyCustomFields(transactionType, originType, originTransaction);

    const hasTagSub = SubscriptionCheck.withKeys(SubscriptionScopeKey.TAG).hasSubscriptionTo();
    const hasMultiCurrencySub =
        SubscriptionCheck.withKeys(SubscriptionScopeKey.MULTI_CURRENCY).hasSubscriptionTo();
    const hasCustomFieldSub =
        SubscriptionCheck.withKeys(SubscriptionScopeKey.CUSTOM_FIELD).hasSubscriptionTo();

    const transaction = {
        ...initData,
        ...originTransaction,
        type: transactionType,

        // Copy from initData
        date: initData.date,
        due_date: initData.due_date, // For bill
        number: initData.number,
        currency_code: hasMultiCurrencySub ? originTransaction.currency_code : companyCurrencyCode,
        exchange_rate: hasMultiCurrencySub ? originTransaction.exchange_rate : 1,

        linked_items: [],
        remarks: initData.remarks,

        // Copy from originTransaction
        status: originTransaction.status,
        number2: originTransaction.number,
        amount: formatNumber(originTransaction.amount, 2, undefined, false),
        account2_id: originTransaction.account2_id || initData.account2_id,

        fields: hasCustomFieldSub ? fields : undefined,
        link_items: linkItems,
        sales_link_items: salesLinkItems,
        purchase_link_items: purchaseLinkItems,

        // Check subscription
        tag_ids: hasTagSub ? originTransaction.tag_ids || [] : undefined,
    };

    const hasTagChange = !_.isEqual(originTransaction.tag_ids || [], transaction.tag_ids || []);
    const hasCurrencyCodeChange = originTransaction.currency_code !== transaction.currency_code;
    // compare the 2 transactions' fields without 'id'
    // fields.length === 0 means that the 2 transactions don't share any same fields
    // const hasFieldChange = (fields || []).length === 0 ? false : _.differenceWith(
    //     originTransaction.fields,
    //     transaction.fields,
    //     (a, b) => _.isEqual(_.omit(a, 'id'), _.omit(b, 'id'))).length !== 0;
    // 'fields' will contain all fields that are present in BOTH origin and target transaction
    const hasFieldChange = !hasCustomFieldSub && !!(fields || []).find(f => f.value);

    let finalFormItems = transaction.form_items;
    let nonBaseUomItems, removedBundles;
    let hasTaxChange, hasLocationChange;

    // Form Items
    if (transaction.form_items) {
        const {
            // processedFormItems,
            finalFormItems: finalFormItemsFunc,
            nonBaseUomItems: nonBaseUomItemsFunc,
            hasTaxChange: hasTaxChangeFunc,
            hasLocationChange: hasLocationChangeFunc,
            removedBundles: removedBundlesFunc,
        } = processFormItems({
            formItems: transaction.form_items || [],
            productList,
            settings,
        });

        finalFormItems = finalFormItemsFunc;
        hasTaxChange = hasTaxChangeFunc;
        hasLocationChange = hasLocationChangeFunc;
        nonBaseUomItems = nonBaseUomItemsFunc;
        removedBundles = removedBundlesFunc;

        // transaction.form_items = transaction.form_items.map(formItem => {
        //     // Ignore subtitle and subtotal lines
        //     if ([RowType.subtotal, RowType.subtitle].includes(formItem.type))
        //         return formItem;

        //     // Remove transfer transaction information
        //     return {
        //         ...formItem,
        //         transfer_transaction: undefined,
        //         transfer_item_id: undefined,
        //         balance_quantity: undefined,
        //         transferred_to_quantity: undefined,
        //     };
        // });
    }

    // From Sales to Purchase (i.e. Invoice => Bill)
    if (getParentUrl(originType) !== getParentUrl(transactionType)) {
        transaction.contact_id = undefined;
        transaction.tag_ids = [];
    }

    // console.log({processedFormItems, finalFormItems, nonBaseUomItems});
    // REMOVE non-base uom items
    if (nonBaseUomItems?.length || removedBundles?.length ||
        hasTagChange || hasCurrencyCodeChange || hasFieldChange || hasLocationChange || hasTaxChange
    ) {
        // yield to make the Modal.confirm synchronous by using Promise
        yield showConfirmationModalPromise(
            nonBaseUomItems,
            removedBundles,
            {
                hasTagChange, hasCurrencyCodeChange, hasFieldChange, hasLocationChange, hasTaxChange,
            })
            .then(() => {
                // User clicked OK - remove invalid form items with non-base UOM
                transaction.form_items = finalFormItems;
            });
    }
    else {
        transaction.form_items = finalFormItems;
    }

    // Remove all ids
    // return deleteAllIds(transaction);
    // Remove all ids
    return {
        isProceed: true,
        transaction: deleteAllIds(transaction),
    };
}
